import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { GraphId, ServerGraphNode } from 'dashboard/models/graph';
import {
  Backend,
  ServerEdge,
  ServerExpandResponse,
  ServerHydrateResponse,
  ServerLightlyHydrateResponse,
  ServerNode,
} from 'dashboard/models/graph-backend';
import { GraphQuery, GraphQueryStatus, sanitizeGraphQuery } from 'dashboard/models/graph-query';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiRequestError from 'lib/errors/ApiRequestError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { ApiEndpoint, ApiModel, ApiPayload } from 'lib/interfaces/api';

export class RealBackend implements Backend {
  liveModeEnabled: boolean;

  constructor(liveModeEnabled: boolean) {
    this.liveModeEnabled = liveModeEnabled;
  }

  async queryWithTemplate({
    graphQueryTemplateId,
    parameterMap,
    graphId,
    creatorId,
    creatorType,
  }: Parameters<Backend['queryWithTemplate']>[0]) {
    const { data: graphQuery } = await SessionClientDashboard.postResourceCreate<GraphQuery>(
      {
        type: ApiModel.GraphQuery,
        attributes: {
          'graph-id': graphId,
          'creator-id': creatorId,
          'creator-type': creatorType,
        },
      },
      {
        meta: {
          'run-sync': false,
          'preload-result': true,
          'graph-query-template-id': graphQueryTemplateId,
          'parameter-map': parameterMap,
        },
      },
    );

    return sanitizeGraphQuery(graphQuery);
  }

  async poll({ graphQueryId }: Parameters<Backend['poll']>[0]): Promise<GraphQuery> {
    const { data: graphQuery } = await SessionClientDashboard.getResource<GraphQuery>(
      ApiEndpoint.GraphQuery,
      graphQueryId,
    );

    return sanitizeGraphQuery(graphQuery);
  }

  async pollContinuously({
    graphQueryId,
    shouldContinue,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onError = null,
    onNetworkError = () => undefined,
    onQueryError = () => undefined,
    onQueryPolled = () => undefined,
    cancelQueryIfStoppedPolling = false,
  }: Parameters<Backend['pollContinuously']>[0]): Promise<GraphQuery | null> {
    /* eslint-disable no-constant-condition */
    while (true) {
      let updatedGraphQuery: GraphQuery;
      try {
        // We will set this back to idle upon error or on AfterRendererLayoutEvent
        updatedGraphQuery = await this.poll({ graphQueryId });
      } catch (err) {
        if (err instanceof ApiNetworkError || err instanceof ApiRequestError) {
          onNetworkError(err);
        } else if (err instanceof ApiResponseError) {
          if (onError) {
            onError(err);
          } else {
            onNetworkError(err);
          }
        }
        return null;
      }

      if (!shouldContinue()) {
        if (cancelQueryIfStoppedPolling) {
          this.cancelQuery(updatedGraphQuery);
        }
        return null;
      }
      switch (updatedGraphQuery.attributes?.status) {
        case GraphQueryStatus.Completed:
          return updatedGraphQuery;
        case GraphQueryStatus.Submitted:
          // try again after some delay
          onQueryPolled(updatedGraphQuery);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          break;
        default:
          onQueryError(updatedGraphQuery);
          return updatedGraphQuery;
      }
    }
  }

  async runQueryAndPoll({
    query,
    graphId,
    creatorId,
    creatorType,
    shouldContinue = () => true,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onError = null,
    onNetworkError = () => undefined,
    onQueryError = () => undefined,
    onQuerySubmitted = () => undefined,
    onQueryPolled = () => undefined,
  }: Parameters<Backend['runQueryAndPoll']>[0]) {
    let response;
    try {
      response = await SessionClientDashboard.postResourceCreate<GraphQuery>(
        {
          type: ApiModel.GraphQuery,
          attributes: {
            'graph-id': graphId,
            'creator-id': creatorId,
            'creator-type': creatorType,
            params: query,
          },
        },
        {
          meta: {
            'run-sync': false,
            'preload-result': true,
          },
        },
      );
    } catch (err) {
      if (err instanceof ApiNetworkError || err instanceof ApiRequestError) {
        onNetworkError(err);
      } else if (err instanceof ApiResponseError) {
        if (onError) {
          onError(err);
        } else {
          onNetworkError(err);
        }
      }
      return null;
    }

    const graphQuery = sanitizeGraphQuery(response.data);

    if (!shouldContinue()) {
      this.cancelQuery(graphQuery);
      return null;
    }

    switch (graphQuery.attributes?.status) {
      case GraphQueryStatus.Completed:
        return graphQuery;
      case GraphQueryStatus.Submitted:
        onQuerySubmitted(graphQuery);
        // poll for result
        return this.pollContinuously({
          graphQueryId: graphQuery.id,
          shouldContinue,
          onNetworkError,
          onQueryError,
          onQueryPolled,
          cancelQueryIfStoppedPolling: true,
        });
      default:
        onQueryError(graphQuery);
        return graphQuery;
    }
  }

  async queryWithTemplateAndPoll({
    graphQueryTemplateId,
    parameterMap,
    graphId,
    creatorId,
    creatorType,
    shouldContinue,
    onNetworkError = () => undefined,
    onQueryError = () => undefined,
    onQueryPolled = () => undefined,
  }: Parameters<Backend['queryWithTemplateAndPoll']>[0]) {
    let response;
    try {
      response = await SessionClientDashboard.postResourceCreate<GraphQuery>(
        {
          type: ApiModel.GraphQuery,
          attributes: {
            'graph-id': graphId,
            'creator-id': creatorId,
            'creator-type': creatorType,
          },
        },
        {
          meta: {
            'run-sync': false,
            'preload-result': true,
            'graph-query-template-id': graphQueryTemplateId,
            'parameter-map': parameterMap,
          },
        },
      );
    } catch (err) {
      if (
        err instanceof ApiNetworkError ||
        err instanceof ApiResponseError ||
        err instanceof ApiRequestError
      ) {
        onNetworkError(err);
      }
      return null;
    }

    const graphQuery = sanitizeGraphQuery(response.data);

    if (!shouldContinue()) {
      this.cancelQuery(graphQuery);
      return null;
    }

    switch (graphQuery.attributes?.status) {
      case GraphQueryStatus.Completed:
        return graphQuery;
      case GraphQueryStatus.Submitted:
        // poll for result
        return this.pollContinuously({
          graphQueryId: graphQuery.id,
          shouldContinue,
          onNetworkError,
          onQueryError,
          onQueryPolled,
          cancelQueryIfStoppedPolling: true,
        });
      default:
        onQueryError(graphQuery);
        return graphQuery;
    }
  }

  async accounts({
    query,
    graphId,
    creatorId,
    creatorType,
  }: Parameters<Backend['accounts']>[0]): Promise<GraphQuery> {
    const { data: graphQuery } = await SessionClientDashboard.postResourceCreate<GraphQuery>(
      {
        type: ApiModel.GraphQuery,
        attributes: {
          'graph-id': graphId,
          params: query,
          'creator-id': creatorId,
          'creator-type': creatorType,
        },
      },
      {
        meta: {
          'run-sync': false,
          'preload-result': true,
        },
      },
    );

    return sanitizeGraphQuery(graphQuery);
  }

  async cluster({
    query,
    graphId,
    creatorId,
    creatorType,
  }: Parameters<Backend['cluster']>[0]): Promise<GraphQuery> {
    const { data: graphQuery } = await SessionClientDashboard.postResourceCreate<GraphQuery>(
      {
        type: ApiModel.GraphQuery,
        attributes: {
          'graph-id': graphId,
          params: query,
          'creator-id': creatorId,
          'creator-type': creatorType,
        },
      },
      {
        meta: {
          'run-sync': false,
          'preload-result': true,
        },
      },
    );

    return sanitizeGraphQuery(graphQuery);
  }

  async nHopsFromAccount({
    query,
    graphId,
    creatorId,
    creatorType,
  }: Parameters<Backend['nHopsFromAccount']>[0]): Promise<GraphQuery> {
    const { data: graphQuery } = await SessionClientDashboard.postResourceCreate<GraphQuery>(
      {
        type: ApiModel.GraphQuery,
        attributes: {
          'graph-id': graphId,
          params: query,
          'creator-id': creatorId,
          'creator-type': creatorType,
        },
      },
      {
        meta: {
          'run-sync': false,
          'preload-result': true,
        },
      },
    );

    return sanitizeGraphQuery(graphQuery);
  }

  async liveQuery(graphId: GraphId): Promise<GraphQuery> {
    if (!this.liveModeEnabled) {
      console.warn('Live query is disabled.');
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return;
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const graphQuery = {
      id: 'live-stubbed',
      type: ApiModel.GraphQuery,
      attributes: {
        'cancelled-at': null,
        'completed-at': new Date().toISOString(),
        'creator-id': 'stubbed_live_query_creator_id',
        'creator-type': 'User',
        'created-at': new Date().toISOString(),
        'error-cause': null,
        'error-message': null,
        'errored-at': null,
        'graph-id': graphId,
        params: {
          type: 'experimental_live_query',
        },
        'query-hint': null,
        'redacted-at': null,
        // Results is always empty for real backend
        results: {
          version: 'graph_v2',
          nodes: [] as ServerNode[],
          edges: [] as ServerEdge[],
          'expandable-nodes': [] as ServerNode[],
          'next-cursor': '',
        },
        status: 'completed',
        'timed-out-at': null,
        'updated-at': new Date().toISOString(),
      },
      relationships: {
        'graph-query-template': {
          data: null,
        },
        'oldest-snapshot': {
          data: null,
        },
      },
    } as GraphQuery;

    return sanitizeGraphQuery(graphQuery);
  }

  customDatumTypesOverride(): null {
    // If we are using the real backend just grab node_config from graph
    return null;
  }

  nodeRenderConfigOverride(): null {
    // If we are using the real backend just grab node_render_config from graph attributes
    return null;
  }

  coloringRulesOverride(): null {
    // If we are using the real backend just grab coloring rules from graph attributes
    return null;
  }

  propertyConfigOverride(): null {
    // If we are using the real backend just grab property configs from graph attributes
    return null;
  }

  async expand({
    nodeId,
    graphId,
    expandDatumTypes,
    existingNodeIds,
  }: Parameters<Backend['expand']>[0]): Promise<ServerExpandResponse> {
    // NOTE: This request should be a GET instead, but we need to pass a ton of data from the frontend to the
    //       backend. At least the URI path looks reasonable: `v1/graph-nodes/:graph_id/expand`.
    const serverResponse = await SessionClientDashboard.postResourceAction<
      ServerGraphNode,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiPayload<unknown, ApiModel.GraphNode>,
      ServerExpandResponse
    >(
      {
        type: ApiModel.GraphNode,
        id: graphId,
      },
      'expand',
      {
        meta: {
          node_value: nodeId.value,
          node_datum_type: nodeId.token,
          expand_datum_types: expandDatumTypes,
          known_nodes: existingNodeIds.map((nodeId) => {
            return {
              datum_type: nodeId.token,
              value: nodeId.value,
            };
          }),
        },
      },
    );
    return new Promise((resolve, _reject) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      resolve(serverResponse.meta);
    });
  }

  async hydrate({
    nodeIds,
    graphId,
    includeAccountFields = false,
  }: Parameters<Backend['hydrate']>[0]): Promise<ServerHydrateResponse> {
    // NOTE: This request should be a GET instead, but the other requests are already POST requests,
    //       and we don't have a simple library function to make the URI path consistent. At least
    //       with POST, the URI path ends with: `v1/graph-nodes/:graph_id/hydrate`
    const serverResponse = await SessionClientDashboard.postResourceAction<
      ServerGraphNode,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiPayload<unknown, ApiModel.GraphNode>,
      ServerHydrateResponse
    >(
      {
        type: ApiModel.GraphNode,
        id: graphId,
      },
      'hydrate',
      {
        meta: {
          nodes: nodeIds.map((nodeId) => {
            return {
              value: nodeId.value,
              datum_type: nodeId.token,
            };
          }),
          include_account_fields: includeAccountFields,
        },
      },
    );
    return new Promise((resolve, _reject) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      resolve(serverResponse.meta);
    });
  }

  async lightlyHydrate({
    nodeIds,
    graphId,
  }: Parameters<Backend['lightlyHydrate']>[0]): Promise<ServerLightlyHydrateResponse> {
    // NOTE: This request should be a GET instead, but we need to pass a ton of data from the frontend to the
    //       backend. At least the URI path looks reasonable: `v1/graph-nodes/:graph_id/lightly-hydrate`.
    const serverResponse = await SessionClientDashboard.postResourceAction<
      ServerGraphNode,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ApiPayload<unknown, ApiModel.GraphNode>,
      ServerLightlyHydrateResponse
    >(
      {
        type: ApiModel.GraphNode,
        id: graphId,
      },
      'lightly-hydrate',
      {
        meta: {
          nodes: nodeIds.map((nodeId) => {
            return {
              value: nodeId.value,
              datum_type: nodeId.token,
            };
          }),
        },
      },
    );
    return new Promise((resolve, _reject) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      resolve(serverResponse.meta);
    });
  }

  private async cancelQuery(graphQuery: GraphQuery) {
    if (graphQuery.attributes?.status === 'submitted') {
      await SessionClientDashboard.postResourceAction<GraphQuery>(graphQuery, 'cancel');
    }
  }
}
